import { t } from "i18next";
import { z } from "zod";

import {
  isMobilePlatform,
  OFFER_PLAN_TYPES,
  PLATFORM_TYPES,
  platformSupportsGeoblock,
  RESOLUTION_TYPES,
} from "./offerPlan.constants";
import {
  currencyMin,
  getPlanFieldState,
} from "./offersFormParams/offersFormParams";

import type {
  CreateOfferPlanToApi,
  OfferPlanToApi,
  OfferPlanTypeValue,
} from "@/src/models/OfferPlan.types";
import type { OfferPlanType } from "@/src/types";

import type { Advertising, ProviderPlan } from "../../api/OfferAPI.types";

type CurrencyCode = keyof typeof currencyMin;

/** Form-editable plan fields — `uuid` is always set here, `advertising` may be null. */
export interface OfferPlanFormFields extends Omit<
  OfferPlanToApi,
  "advertising" | "uuid"
> {
  uuid: string;
  advertising?: Advertising | null;
}

export function planFormToApi(plan: OfferPlanFormFields): OfferPlanToApi {
  return {
    type: plan.type,
    uuid: plan.uuid,
    advertising: plan.advertising,
    countries: plan.countries,
    country_groups: plan.country_groups,
    currency: plan.currency,
    donation_enabled: plan.donation_enabled,
    gift_enabled: plan.gift_enabled,
    d2g_enabled: plan.d2g_enabled,
    enabled: plan.enabled,
    frequency: plan.frequency,
    geoblock_mode: plan.geoblock_mode,
    limit_date: plan.limit_date,
    platform: plan.platform,
    platform_product_id: plan.platform_product_id,
    price: plan.price,
    rental_duration: plan.rental_duration,
    resolution: plan.resolution,
    trial_duration: plan.trial_duration,
    storefront_retailer_uuid: plan.storefront_retailer_uuid,
    require_registration: plan.require_registration,
    provider_plans: plan.provider_plans,
    freemium: plan.freemium,
  };
}

/** A new plan has no server uuid yet: it's attached to its offer instead. */
export function planFormToCreateApi(
  plan: OfferPlanFormFields,
  offerUuid: string,
): CreateOfferPlanToApi {
  const { uuid, ...payload } = planFormToApi(plan);

  return { ...payload, offer_uuid: offerUuid };
}

/** The `OfferPlanType` fields the form doesn't edit, kept through a save. */
type PlanReadOnlyFields = Partial<
  Pick<
    OfferPlanType,
    | "isNew"
    | "description"
    | "inserted_at"
    | "updated_at"
    | "gift_platform_price_id"
    | "access_duration_type"
    | "purchases"
    | "subscriptions"
    | "free"
  >
>;

/** A plan once it's gone through the drawer's edit/save cycle. */
export type FlatOfferPlan = OfferPlanFormFields & PlanReadOnlyFields;

const countrySchema = z.object({
  uuid: z.string(),
  country: z.string().optional(),
});

const countryGroupSchema = z.object({
  uuid: z.string(),
  label: z.string().optional(),
});

export const planSchema = z
  .object({
    type: z.enum(["svod", "tvod_bundle", "free"]),
    uuid: z.string(),
    isNew: z.boolean().optional(),
    platform: z.enum(["okast", "apple", "google", "retailer", "edu"]),
    enabled: z.boolean(),
    resolution: z.string(),
    price: z.number(),
    currency: z.string(),
    require_registration: z.boolean(),
    frequency: z.number().optional(),
    rental_duration: z.number().optional(),
    limit_date: z.union([z.string(), z.number()]).nullable(),
    geoblock_mode: z.enum(["allow", "deny", "disabled"]).optional(),
    countries: z.array(countrySchema),
    country_groups: z.array(countryGroupSchema),
    donation_enabled: z.boolean().optional(),
    gift_enabled: z.boolean().optional(),
    d2g_enabled: z.boolean().optional(),
    trial_duration: z.number().optional(),
    platform_product_id: z.string().optional(),
    storefront_retailer_uuid: z.string().optional(),
    provider_plans: z.array(z.enum(["stripe", "paypal"])).optional(),
    advertising: z.custom<Advertising>().nullable().optional(),
    // Form-only: mirrors the monetization switch, not sent by planFormToApi.
    monetization_enabled: z.boolean(),
    freemium: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const fieldState = getPlanFieldState(data);

    if (!fieldState.price.hidden) {
      const min = currencyMin[data.currency as CurrencyCode];
      if (min !== undefined && data.price < min) {
        ctx.addIssue({
          code: "custom",
          message: t("editor-errors:minValue", { min }),
          path: ["price"],
        });
      }
    }

    if (!fieldState.platform_product_id.hidden && !data.platform_product_id) {
      ctx.addIssue({
        code: "custom",
        message: t("editor-errors:requiredField"),
        path: ["platform_product_id"],
      });
    }

    if (
      !fieldState.storefront_retailer_uuid.hidden &&
      !data.storefront_retailer_uuid
    ) {
      ctx.addIssue({
        code: "custom",
        message: t("editor-errors:requiredField"),
        path: ["storefront_retailer_uuid"],
      });
    }

    if (data.monetization_enabled && !data.advertising) {
      ctx.addIssue({
        code: "custom",
        message: t("editor-errors:requiredField"),
        path: ["advertising"],
      });
    }

    if (
      (data.geoblock_mode === "allow" || data.geoblock_mode === "deny") &&
      data.countries.length === 0 &&
      data.country_groups.length === 0
    ) {
      ctx.addIssue({
        code: "custom",
        message: t("editor-errors:requiredField"),
        path: ["countries"],
      });
    }
  });

export type PlanFormValues = z.infer<typeof planSchema>;

/** `planSchema` plus the payment-method rule, which needs the project's configured providers. */
export function createPlanSchema(availableProviderPlanList: ProviderPlan[]) {
  return planSchema.superRefine((data, ctx) => {
    const fieldState = getPlanFieldState(data);
    if (
      !fieldState.provider_plans.hidden &&
      availableProviderPlanList.length > 0 &&
      (data.provider_plans?.length ?? 0) === 0
    ) {
      ctx.addIssue({
        code: "custom",
        message: t("editor-errors:noProviderPlan"),
        path: ["provider_plans"],
      });
    }
  });
}

// frequency/platform_product_id spelled out to avoid widening back to the raw plan's nullable type.
export function mergePlanWithFormValues(
  plan: OfferPlanType,
  formValues: PlanFormValues,
): PlanFormValues & PlanReadOnlyFields {
  return {
    ...plan,
    ...formValues,
    frequency: formValues.frequency,
    platform_product_id: formValues.platform_product_id,
  };
}

function roundPrice(price: number): number {
  return price % 1 !== 0 ? Number(Number(price).toFixed(2)) : Number(price);
}

// Falls back on any falsy value (0, "", null, undefined), not just nullish ones.
function orDefault<T>(value: T | null | undefined, fallback: T): T {
  if (value) return value;
  return fallback;
}

export function apiToPlanForm(offerPlan: OfferPlanType): PlanFormValues {
  const { type, platform } = offerPlan;

  const isSvod = type === OFFER_PLAN_TYPES.SVOD;
  const isTvod = type === OFFER_PLAN_TYPES.TVOD;
  const isFree = type === OFFER_PLAN_TYPES.FREE;

  const isOkast = platform === PLATFORM_TYPES.OKAST;
  const isMobile = isMobilePlatform(platform);
  const isRetailer = platform === PLATFORM_TYPES.RETAILER;
  const isEdu = platform === PLATFORM_TYPES.EDU;

  // Geoblock/countries only ever come from the api on free plans or geoblock platforms.
  const geoblockApplies = isFree || platformSupportsGeoblock(platform);
  // limit_date only ever comes from the api on tvod, free or okast plans.
  const limitDateApplies = isTvod || isFree || isOkast;

  let frequency: number | undefined;
  let rentalDuration: number | undefined;

  if (isSvod) {
    frequency = isMobile ? undefined : orDefault(offerPlan.frequency, 1);
    rentalDuration = 0;
  } else if (isTvod) {
    frequency = 0;
    // Rental duration is set by the store on mobile — unknown here, same as frequency above.
    rentalDuration = isMobile
      ? undefined
      : Number(
          offerPlan.limit_date ? 0 : orDefault(offerPlan.rental_duration, 7),
        );
  } else if (isFree) {
    rentalDuration = Number(offerPlan.limit_date ? 0 : -1);
  }

  let price = roundPrice(offerPlan.price ?? 0);
  if (isOkast) {
    price = isFree ? 0 : roundPrice(orDefault(offerPlan.price, 0.5));
  }

  return {
    type,
    uuid: offerPlan.uuid,
    isNew: offerPlan.isNew ?? false,
    platform,
    // An edu offer's own status alone decides its availability.
    enabled: isEdu || (offerPlan.enabled ?? false),
    resolution: offerPlan.resolution ?? RESOLUTION_TYPES.HD,
    price,
    currency: isOkast ? orDefault(offerPlan.currency, "EUR") : "EUR",
    require_registration: offerPlan.require_registration ?? true,
    frequency,
    rental_duration: rentalDuration,
    limit_date: (limitDateApplies ? offerPlan.limit_date : undefined) ?? null,
    geoblock_mode: geoblockApplies
      ? (offerPlan.geoblock_mode ?? "disabled")
      : "disabled",
    countries: geoblockApplies ? (offerPlan.countries ?? []) : [],
    country_groups: geoblockApplies ? (offerPlan.country_groups ?? []) : [],
    donation_enabled: isOkast
      ? (offerPlan.donation_enabled ?? false)
      : undefined,
    gift_enabled: isOkast ? (offerPlan.gift_enabled ?? true) : undefined,
    d2g_enabled: offerPlan.d2g_enabled ?? false,
    trial_duration:
      isOkast && isSvod ? (offerPlan.trial_duration ?? 0) : undefined,
    platform_product_id: isMobile
      ? (offerPlan.platform_product_id ?? "")
      : undefined,
    storefront_retailer_uuid: isRetailer
      ? (offerPlan.storefront_retailer_uuid ?? "")
      : undefined,
    provider_plans: offerPlan.provider_plans,
    advertising: offerPlan.advertising ?? null,
    monetization_enabled: Boolean(offerPlan.advertising),
    freemium: isFree ? (offerPlan.freemium ?? false) : undefined,
  };
}

export function getNewOfferPlan({
  offerPlanType,
  providerPlans = [],
}: {
  offerPlanType: OfferPlanTypeValue;
  providerPlans?: ProviderPlan[];
}): OfferPlanType {
  const tempId = Math.floor(Math.random() * 1000000).toString();

  return {
    isNew: true,
    uuid: tempId,
    platform: "okast",
    type: offerPlanType,
    enabled: false,
    resolution: RESOLUTION_TYPES.HD,
    require_registration: true,
    provider_plans: offerPlanType === "free" ? undefined : providerPlans,
  };
}
